/**
 * ConfigDropdown - Wiederverwendbare Dropdown-Komponente fuer Config Menus
 *
 * Varianten:
 * - simple: Einfaches Dropdown ohne Suche (fuer kleine Listen)
 * - searchable: Dropdown mit Suchfeld (fuer grosse Listen)
 *
 * Features:
 * - Gruppierung von Items (optional)
 * - Keyboard Navigation
 * - Konsistentes Styling mit AFFiNE Design System
 *
 * E018-S008-T009: Initial implementation
 */
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';

export interface DropdownOption {
  id: string;
  label: string;
  group?: string;
  icon?: string;
  disabled?: boolean;
}

export interface DropdownGroup {
  id: string;
  label: string;
  isCurrent?: boolean;
}

export class ConfigDropdown extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .config-dropdown {
      position: relative;
      width: 100%;
    }

    .config-dropdown-trigger {
      width: 100%;
      padding: 8px 32px 8px 12px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      color: var(--streamlain-v2-text-primary);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      position: relative;
    }

    .config-dropdown-trigger::after {
      content: '';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid var(--streamlain-v2-icon-primary);
      transition: transform 0.15s ease;
    }

    .config-dropdown-trigger.open::after {
      transform: translateY(-50%) rotate(180deg);
    }

    .config-dropdown-trigger:hover:not(:disabled) {
      border-color: var(--streamlain-v2-brand-primary);
    }

    .config-dropdown-trigger:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .config-dropdown-trigger.placeholder {
      color: var(--streamlain-v2-text-placeholder);
    }

    .config-dropdown-panel {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 4px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-primary);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 100;
      overflow: hidden;
    }

    .config-dropdown-search {
      padding: 8px;
      border-bottom: 1px solid var(--streamlain-v2-layer-insideBorder-border);
    }

    .config-dropdown-search input {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 6px;
      background: var(--streamlain-v2-layer-background-secondary);
      color: var(--streamlain-v2-text-primary);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      box-sizing: border-box;
    }

    .config-dropdown-search input:focus {
      outline: none;
      border-color: var(--streamlain-v2-brand-primary);
    }

    .config-dropdown-search input::placeholder {
      color: var(--streamlain-v2-text-placeholder);
    }

    .config-dropdown-list {
      max-height: 200px;
      overflow-y: auto;
      padding: 4px;
    }

    .config-dropdown-list::-webkit-scrollbar {
      width: 6px;
    }

    .config-dropdown-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .config-dropdown-list::-webkit-scrollbar-thumb {
      background: var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 3px;
    }

    .config-dropdown-group {
      margin-bottom: 4px;
    }

    .config-dropdown-group-header {
      font-size: 11px;
      font-weight: 500;
      color: var(--streamlain-v2-text-secondary);
      padding: 6px 8px 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .config-dropdown-group-header.current {
      color: var(--streamlain-v2-brand-primary);
    }

    .config-dropdown-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: 1px solid transparent;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s ease;
      font-size: 13px;
      color: var(--streamlain-v2-text-primary);
      background: transparent;
      width: 100%;
      text-align: left;
    }

    .config-dropdown-item:hover:not(:disabled) {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      border-color: var(--streamlain-v2-layer-insideBorder-border);
    }

    .config-dropdown-item.selected {
      background: var(--streamlain-v2-brand-primaryTint);
      border-color: var(--streamlain-v2-brand-primary);
    }

    .config-dropdown-item:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .config-dropdown-item-icon {
      font-size: 14px;
      width: 20px;
      text-align: center;
      flex-shrink: 0;
    }

    .config-dropdown-item-label {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .config-dropdown-empty {
      padding: 16px;
      text-align: center;
      color: var(--streamlain-v2-text-secondary);
      font-size: 13px;
    }
  `;

  @property({ attribute: false })
  accessor options: DropdownOption[] = [];

  @property({ attribute: false })
  accessor groups: DropdownGroup[] = [];

  @property({ type: String })
  accessor value = '';

  @property({ type: String })
  accessor placeholder = 'Select...';

  @property({ type: Boolean })
  accessor searchable = false;

  @property({ type: Boolean })
  accessor disabled = false;

  @property({ attribute: false })
  accessor onChange: ((value: string) => void) | undefined;

  @state()
  accessor _isOpen = false;

  @state()
  accessor _searchQuery = '';

  private _handleTriggerClick(e: Event) {
    e.stopPropagation();
    if (this.disabled) return;
    this._isOpen = !this._isOpen;
    if (!this._isOpen) {
      this._searchQuery = '';
    }
  }

  private _handleItemClick(e: Event, optionId: string) {
    e.stopPropagation();
    this.onChange?.(optionId);
    this._isOpen = false;
    this._searchQuery = '';
  }

  private _handleSearchInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._searchQuery = input.value;
  }

  private _handleWheel(e: WheelEvent) {
    e.stopPropagation();
  }

  private get selectedOption(): DropdownOption | undefined {
    return this.options.find(opt => opt.id === this.value);
  }

  private get filteredOptions(): DropdownOption[] {
    if (!this._searchQuery.trim()) {
      return this.options;
    }
    const query = this._searchQuery.toLowerCase();
    return this.options.filter(
      opt =>
        opt.label.toLowerCase().includes(query) ||
        opt.group?.toLowerCase().includes(query)
    );
  }

  private get groupedOptions(): Map<string, DropdownOption[]> {
    const grouped = new Map<string, DropdownOption[]>();
    const filtered = this.filteredOptions;

    if (this.groups.length === 0) {
      // No grouping, use single default group
      grouped.set('', filtered);
    } else {
      // Initialize groups in order
      for (const group of this.groups) {
        grouped.set(group.id, []);
      }
      // Assign options to groups
      for (const option of filtered) {
        const groupId = option.group || '';
        const groupOptions = grouped.get(groupId) || [];
        groupOptions.push(option);
        grouped.set(groupId, groupOptions);
      }
    }

    return grouped;
  }

  private _renderTrigger() {
    const selected = this.selectedOption;
    const displayText = selected?.label || this.placeholder;
    const isPlaceholder = !selected;

    return html`
      <button
        type="button"
        class="config-dropdown-trigger ${this._isOpen
          ? 'open'
          : ''} ${isPlaceholder ? 'placeholder' : ''}"
        ?disabled=${this.disabled}
        @click=${this._handleTriggerClick}
        @mousedown=${(e: Event) => e.stopPropagation()}
      >
        ${selected?.icon
          ? html`<span class="config-dropdown-item-icon"
              >${selected.icon}</span
            >`
          : nothing}
        ${displayText}
      </button>
    `;
  }

  private _renderSearch() {
    if (!this.searchable) return nothing;

    return html`
      <div class="config-dropdown-search">
        <input
          type="text"
          placeholder="Search..."
          .value=${this._searchQuery}
          @input=${this._handleSearchInput}
          @click=${(e: Event) => e.stopPropagation()}
          @mousedown=${(e: Event) => e.stopPropagation()}
        />
      </div>
    `;
  }

  private _renderItems() {
    const grouped = this.groupedOptions;

    if (this.filteredOptions.length === 0) {
      return html`
        <div class="config-dropdown-empty">
          ${this._searchQuery ? 'No matches found' : 'No options available'}
        </div>
      `;
    }

    // Render without group headers if no groups defined
    if (this.groups.length === 0) {
      const options = grouped.get('') || [];
      return options.map(opt => this._renderItem(opt));
    }

    // Render with group headers
    return this.groups.map(group => {
      const options = grouped.get(group.id) || [];
      if (options.length === 0) return nothing;

      return html`
        <div class="config-dropdown-group">
          <div
            class="config-dropdown-group-header ${group.isCurrent
              ? 'current'
              : ''}"
          >
            ${group.label}
          </div>
          ${options.map(opt => this._renderItem(opt))}
        </div>
      `;
    });
  }

  private _renderItem(option: DropdownOption) {
    const isSelected = option.id === this.value;

    return html`
      <button
        type="button"
        class="config-dropdown-item ${isSelected ? 'selected' : ''}"
        ?disabled=${option.disabled}
        @click=${(e: Event) => this._handleItemClick(e, option.id)}
        @mousedown=${(e: Event) => e.stopPropagation()}
      >
        ${option.icon
          ? html`<span class="config-dropdown-item-icon">${option.icon}</span>`
          : nothing}
        <span class="config-dropdown-item-label">${option.label}</span>
      </button>
    `;
  }

  private _renderPanel() {
    if (!this._isOpen) return nothing;

    return html`
      <div
        class="config-dropdown-panel"
        @click=${(e: Event) => e.stopPropagation()}
        @mousedown=${(e: Event) => e.stopPropagation()}
      >
        ${this._renderSearch()}
        <div class="config-dropdown-list" @wheel=${this._handleWheel}>
          ${this._renderItems()}
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      <div class="config-dropdown">
        ${this._renderTrigger()} ${this._renderPanel()}
      </div>
    `;
  }
}

// Register the component
if (!customElements.get('config-dropdown')) {
  customElements.define('config-dropdown', ConfigDropdown);
}
